import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, inr, fmtDT, today } from "@/lib/api";
import { PageHeader, Sel, Tabs, Kpi, Section, Field, Empty } from "@/components/common";

const M = [["calls", "Total calls"], ["connected", "Connected"], ["no_answer", "No answers"], ["followups_completed", "Follow-ups done"],
  ["followups_pending", "Follow-ups pending"], ["new_leads", "New leads"], ["meetings_scheduled", "Meetings scheduled"], ["meetings_completed", "Meetings completed"],
  ["proposals", "Proposals sent"], ["negotiations", "Negotiations"], ["won", "Deals won"], ["lost", "Deals lost"], ["revenue", "Revenue closed"]];

const Metrics = ({ m }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7" data-testid="eod-metrics">
    {M.map(([k, l]) => <Kpi key={k} testid={`eod-metric-${k}`} label={l} value={k === "revenue" ? inr(m[k]) : m[k]} />)}
  </div>
);

const ReportCard = ({ r }) => (
  <Section title={`${r.user_name} — ${r.date}`} action={<span className="text-[11px] text-muted-foreground">Submitted {fmtDT(r.submitted_at)}</span>}>
    <div className="grid gap-4 p-4 md:grid-cols-2">
      {[["Key updates", r.key_updates], ["Client feedback", r.client_feedback], ["Problems / blockers", r.blockers], ["Tomorrow's priorities", r.tomorrow]].map(([l, v]) => (
        <div key={l}><p className="crm-label">{l}</p><p className="whitespace-pre-wrap text-sm">{v || "—"}</p></div>
      ))}
      <p className="num text-xs text-muted-foreground md:col-span-2">{r.metrics.calls} calls · {r.metrics.connected} connected · {r.metrics.meetings_completed} meetings done · {r.metrics.won} won · {inr(r.metrics.revenue)}</p>
    </div>
  </Section>
);

const MyEOD = () => {
  const [date, setDate] = useState(today());
  const [d, setD] = useState(null);
  const [f, setF] = useState({ key_updates: "", client_feedback: "", blockers: "", tomorrow: "" });
  const [hist, setHist] = useState([]);
  const load = useCallback(() => {
    api.get("/eod/metrics", { params: { date } }).then((r) => {
      setD(r.data);
      const rep = r.data.report || {};
      setF({ key_updates: rep.key_updates || "", client_feedback: rep.client_feedback || "", blockers: rep.blockers || "", tomorrow: rep.tomorrow || "" });
    });
    api.get("/eod").then((r) => setHist(r.data));
  }, [date]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    try { await api.post("/eod", { date, ...f }); toast.success("EOD submitted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <input type="date" data-testid="eod-date" className="crm-input w-44" value={date} onChange={(e) => setDate(e.target.value)} />
        {d?.report && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="eod-submitted-flag"><CheckCircle2 className="h-4 w-4" /> Submitted {fmtDT(d.report.submitted_at)}</span>}
      </div>
      {d && <Metrics m={d.metrics} />}
      <Section title="Your update">
        <div className="grid gap-4 p-4 md:grid-cols-2">
          <Field label="Key updates"><textarea data-testid="eod-key-updates" rows={3} className="crm-input h-auto py-2" value={f.key_updates} onChange={set("key_updates")} /></Field>
          <Field label="Important client feedback"><textarea data-testid="eod-client-feedback" rows={3} className="crm-input h-auto py-2" value={f.client_feedback} onChange={set("client_feedback")} /></Field>
          <Field label="Problems / blockers"><textarea data-testid="eod-blockers" rows={3} className="crm-input h-auto py-2" value={f.blockers} onChange={set("blockers")} /></Field>
          <Field label="Tomorrow's priorities"><textarea data-testid="eod-tomorrow" rows={3} className="crm-input h-auto py-2" value={f.tomorrow} onChange={set("tomorrow")} /></Field>
          <div className="md:col-span-2"><button data-testid="eod-submit-button" onClick={submit} className="h-10 rounded-md bg-primary px-6 text-sm font-semibold text-white hover:opacity-90">{d?.report ? "Update EOD" : "Submit EOD"}</button></div>
        </div>
      </Section>
      <h3 className="text-sm font-bold text-slate-700">Previous reports</h3>
      <div className="space-y-4">{hist.slice(0, 10).map((r) => <ReportCard key={r.id || r.date} r={r} />)}{!hist.length && <Empty text="No EOD reports yet" />}</div>
    </div>
  );
};

const AdminEOD = () => {
  const { salesUsers } = useAuth();
  const [sp] = useSearchParams();
  const [date, setDate] = useState(sp.get("date") || today());
  const [uid, setUid] = useState(sp.get("user") || "");
  const [tab, setTab] = useState("compare");
  const [cmp, setCmp] = useState(null);
  const [reps, setReps] = useState([]);
  useEffect(() => {
    api.get("/eod/compare", { params: { date } }).then((r) => setCmp(r.data));
    api.get("/eod", { params: { date: uid ? undefined : date, user_id: uid || undefined } }).then((r) => setReps(r.data));
  }, [date, uid]);
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-3">
        <input type="date" data-testid="eod-admin-date" className="crm-input w-44" value={date} onChange={(e) => setDate(e.target.value)} />
        <div className="w-44"><Sel testid="eod-admin-salesperson" value={uid} onChange={setUid} placeholder="All salespeople" options={salesUsers.map((u) => ({ value: u.id, label: u.name }))} /></div>
      </div>
      <Tabs testid="eod-admin-tabs" value={tab} onChange={setTab} tabs={[{ value: "compare", label: "Compare daily activity" }, { value: "reports", label: `EOD reports (${reps.length})` }]} />
      {tab === "compare" && cmp && (
        <div className="crm-card overflow-x-auto">
          <table className="crm-table" data-testid="eod-compare-table">
            <thead><tr><th>Salesperson</th><th>EOD</th>{M.map(([, l]) => <th key={l}>{l}</th>)}</tr></thead>
            <tbody>{cmp.rows.map((r) => (
              <tr key={r.user_id}><td className="font-semibold">{r.name}</td>
                <td>{r.submitted ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-slate-300" />}</td>
                {M.map(([k]) => <td key={k} className="num">{k === "revenue" ? inr(r.metrics[k]) : r.metrics[k]}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {tab === "reports" && <div className="space-y-4" data-testid="eod-reports-list">{reps.map((r) => <ReportCard key={r.id || r.user_id + r.date} r={r} />)}{!reps.length && <Empty text="No EOD reports submitted" />}</div>}
    </div>
  );
};

export default function EOD() {
  const { isAdmin } = useAuth();
  return (
    <div>
      <PageHeader title="EOD sales activity" subtitle={isAdmin ? "End-of-day reports and daily activity comparison" : "Your day, auto-calculated — add your notes and submit"} />
      {isAdmin ? <AdminEOD /> : <MyEOD />}
    </div>
  );
}
